<template>
<div class="manage-bank">
    <h2>Банковские счета</h2>
    <WaitMessage :ob="listob" />
    <button v-if="listob.message" type="button" class="btn" :disabled="busy" @click="load()">Повторить загрузку</button>

    <div v-if="!listob.wait && !listob.message">
        <p v-if="!accounts.length">Банковские счета не добавлены</p>
        <div v-for="account in accounts" :key="account.id" class="account">
            <label>
                <input type="radio" name="bank-account" :value="account.id"
                    v-model="selectedId" :disabled="busy" @click="choose(account)" />
                <span>Счёт {{account.anumber}}<br />БИК {{account.bik}}</span>
            </label>
            <button type="button" class="btndot" :disabled="busy" @click="remove(account)">Удалить</button>
        </div>
    </div>
    <WaitMessage :ob="actionob" />

    <h3>Добавить счёт</h3>
    <form @submit.prevent="add">
        <label class="line">
            <span>Номер счёта</span>
            <input type="text" inputmode="numeric" name="anumber" size="20"
                v-model="anumber" :disabled="busy" />
        </label>
        <label class="line">
            <span>БИК</span>
            <input type="text" inputmode="numeric" name="bik" size="9"
                v-model="bik" :disabled="busy" />
        </label>
        <div v-if="validationMessage" class="error-list" role="alert">{{validationMessage}}</div>
        <WaitMessage :ob="addob" />
        <div class="buttons">
            <button type="submit" class="btn btn--blue" :disabled="busy || !!listob.message">Добавить</button>
            <button type="button" class="btn" :disabled="busy" @click="back">Вернуться</button>
        </div>
    </form>
</div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router/composables'
import {
    listWithdrawalBankAccount, setWithdrawalBankAccount,
    delWithdrawalBankAccount, addWithdrawalBankAccount,
} from '@/actions.js'
import WaitMessage from '@/comp/Payout/WaitMessage.vue'

const router = useRouter()
const accounts = ref([])
const selectedId = ref(null)
const anumber = ref('')
const bik = ref('')
const validationMessage = ref('')
const listob = reactive({ wait: true, message: '' })
const actionob = reactive({ wait: false, message: '' })
const addob = reactive({ wait: false, message: '' })
const busy = computed(() => listob.wait || actionob.wait || addob.wait)
let active = true
let loadVersion = 0

onBeforeUnmount(() => {
    active = false
    loadVersion++
})

const errorText = error => typeof error === 'string' ? error : (error && error.message) || 'не удалось'
const firstId = () => accounts.value.length ? accounts.value[0].id : null
const back = () => router.push({ name: 'Start' })

const load = async (afterDelete = false) => {
    const version = ++loadVersion
    listob.wait = true
    listob.message = ''
    accounts.value = []
    selectedId.value = null
    try {
        const { list } = await listWithdrawalBankAccount()
        if (!active || version !== loadVersion) return
        accounts.value = list
        selectedId.value = firstId()
    } catch (error) {
        if (!active || version !== loadVersion) return
        listob.message = `${afterDelete ? 'Счёт удалён, но обновить список не удалось' : 'Не удалось загрузить счета'}: ${errorText(error)}`
    } finally {
        if (active && version === loadVersion) listob.wait = false
    }
}

const choose = async account => {
    if (busy.value || listob.message) return
    selectedId.value = account.id
    actionob.wait = true
    actionob.message = ''
    try {
        await setWithdrawalBankAccount({ id: account.id })
    } catch (error) {
        if (!active) return
        selectedId.value = firstId()
        actionob.message = `Выбрать счёт не удалось: ${errorText(error)}`
        actionob.wait = false
        return
    }
    if (active) back()
}

const remove = async account => {
    if (busy.value || listob.message) return
    actionob.wait = true
    actionob.message = ''
    try {
        await delWithdrawalBankAccount({ id: account.id })
    } catch (error) {
        if (!active) return
        actionob.message = `Удалить счёт не удалось: ${errorText(error)}`
        actionob.wait = false
        return
    }
    if (!active) return
    await load(true)
    if (active) actionob.wait = false
}

const add = async () => {
    if (busy.value || listob.message) return
    const account = {
        bik: bik.value.replace(/\s/g, ''),
        anumber: anumber.value.replace(/\s/g, ''),
    }
    const errors = []
    if (!/^[0-9]{20}$/.test(account.anumber)) errors.push('Номер счёта должен состоять из 20 цифр')
    if (!/^[0-9]{9}$/.test(account.bik)) errors.push('БИК должен состоять из 9 цифр')
    validationMessage.value = errors.join('. ')
    addob.message = ''
    if (errors.length) return
    addob.wait = true
    try {
        await addWithdrawalBankAccount(account)
    } catch (error) {
        if (!active) return
        addob.message = `Добавить счёт не удалось: ${errorText(error)}`
        addob.wait = false
        return
    }
    if (active) back()
}

onMounted(() => load())
</script>

<style lang="scss" scoped>
.manage-bank {
    max-width: 600px;
    .account {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 15px;
        label { display: flex; align-items: center; gap: 10px; }
    }
    .line {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
        margin-bottom: 10px;
        span { flex-basis: 150px; font-weight: bold; }
        input { max-width: 100%; box-sizing: border-box; }
    }
    .buttons { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 20px; }
}
</style>
